import type { OnboardingDraftItem, OnboardingRun } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import { StatusBadge } from '@hostesa/ui';
import { namespace } from './i18n';
import { activationProgress, blockingItemsRemaining } from './lib';

/**
 * ActivationGateChecklist — the pre-activation fact-sheet. Each check shows a
 * pass/pending StatusBadge (text label always present, never color-only) and a
 * progress bar derived from the run counters.
 */
export function ActivationGateChecklist({
  run,
  items,
}: {
  run: OnboardingRun;
  items: readonly OnboardingDraftItem[];
}) {
  const { t } = useT(namespace);

  const requiredFieldsConfirmed = blockingItemsRemaining(items) === 0;
  // Backend pipeline checks surface on the run once it advances past review.
  const retrievalSmokePassed = run.status === 'ready_for_test' || run.status === 'activated';
  const syntheticCallPassed = run.status === 'activated';

  const progress = Math.round(activationProgress(run) * 100);

  const checks: ReadonlyArray<{ key: string; label: string; passed: boolean }> = [
    {
      key: 'required-fields',
      label: t('activate.requiredFieldsConfirmed'),
      passed: requiredFieldsConfirmed,
    },
    {
      key: 'retrieval-smoke',
      label: t('activate.retrievalSmokePassed'),
      passed: retrievalSmokePassed,
    },
    {
      key: 'synthetic-call',
      label: t('activate.syntheticCallPassed'),
      passed: syntheticCallPassed,
    },
  ];

  return (
    <section
      data-testid="onboarding-activation-gate"
      className="flex flex-col gap-4 rounded-lg border border-[var(--color-warm-stone-soft)] p-4"
    >
      <div className="flex flex-col gap-1">
        <h3 className="font-medium text-[var(--color-charcoal)]">{t('activate.gateTitle')}</h3>
        <p className="text-sm text-[var(--color-stone-mid)]">{t('activate.gateDescription')}</p>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between text-xs text-[var(--color-stone-mid)]">
          <span>{t('activate.gateProgress')}</span>
          <span data-testid="onboarding-activation-progress-value">{progress}%</span>
        </div>
        <div
          data-testid="onboarding-activation-progress"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-2 w-full overflow-hidden rounded-full bg-[var(--color-stone-100)]"
        >
          <div
            className="h-full rounded-full bg-[var(--color-charcoal)]"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <ul data-testid="onboarding-activation-checklist" className="flex flex-col gap-2">
        {checks.map((check) => (
          <li
            key={check.key}
            data-testid={`onboarding-gate-check-${check.key}`}
            className="flex items-center justify-between"
          >
            <span className="text-sm text-[var(--color-charcoal)]">{check.label}</span>
            <StatusBadge
              data-testid={`onboarding-gate-check-status-${check.key}`}
              status={check.passed ? 'success' : 'pending'}
              label={check.passed ? t('activate.checkPassed') : t('activate.checkPending')}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
